import React, { useRef } from 'react';
import { CheckCircleIcon, AwardIcon, ShieldCheckIcon, DoorOpenIcon, UsersIcon, HeartIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
type DifferenceItemProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
  isInView: boolean;
};
function DifferenceItem({
  icon,
  title,
  description,
  delay,
  isInView
}: DifferenceItemProps) {
  return <motion.div initial={{
    opacity: 0,
    x: -20
  }} animate={isInView ? {
    opacity: 1,
    x: 0
  } : {}} transition={{
    duration: 0.5,
    delay
  }} className="flex items-start gap-5" data-id="element-20">
      <div className="flex-shrink-0 w-12 h-12 bg-[#c41e3a]/10 border border-[#c41e3a]/30 rounded-lg flex items-center justify-center text-[#c41e3a]" data-id="element-21">
        {icon}
      </div>
      <div data-id="element-22">
        <h3 className="text-white font-bold text-xl mb-1" style={{
        fontFamily: 'Oswald, sans-serif',
        letterSpacing: '0.04em'
      }} data-id="element-23">
          {title}
        </h3>
        <p className="text-gray-400 text-sm leading-relaxed" data-id="element-24">{description}</p>
      </div>
    </motion.div>;
}
export function Difference() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section id="about" className="w-full bg-[#0d0d0d] py-20 md:py-28 px-4 overflow-hidden" data-id="element-25">
      <div className="max-w-6xl mx-auto" data-id="element-26">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-27">
          {/* Left: Text content */}
          <div ref={ref} data-id="element-28">
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5
          }} className="text-[#c41e3a] font-semibold tracking-[0.25em] uppercase text-sm mb-3" style={{
            fontFamily: 'Oswald, sans-serif'
          }} data-id="element-29">
              Why Choose Us
            </motion.p>
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight" style={{
            fontFamily: 'Oswald, sans-serif'
          }} data-id="element-30">
              THE JONATHAN'S
              <br data-id="element-31" />
              <span className="text-gradient-gold" data-id="element-32">DIFFERENCE</span>
            </motion.h2>
            <div className="section-divider w-24 mb-8" data-id="element-33" />
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-gray-400 text-lg leading-relaxed mb-10" data-id="element-34">
              We don't just cut hair. We build confidence. Every man who sits in
              our chair leaves looking better than when he walked in. That's not
              a promise — that's our track record.
            </motion.p>

            <div className="flex flex-col gap-7" data-id="element-35">
              <DifferenceItem icon={<AwardIcon size={22} data-id="element-37" />} title="Experienced Team" description="Professional barbers who take pride in their craft and stay sharp on every technique." delay={0.3} isInView={isInView} data-id="element-36" />
              <DifferenceItem icon={<ShieldCheckIcon size={22} data-id="element-39" />} title="Clean & Comfortable" description="A shop you'll actually enjoy spending time in — clean, welcoming, and professional." delay={0.4} isInView={isInView} data-id="element-38" />
              <DifferenceItem icon={<DoorOpenIcon size={22} data-id="element-41" />} title="Walk-Ins Always Welcome" description="No appointment? No problem. Come in whenever you're ready — we'll take care of you." delay={0.5} isInView={isInView} data-id="element-40" />
              <DifferenceItem icon={<UsersIcon size={22} data-id="element-43" />} title="Every Style, Every Age" description="Men, boys, girls — everyone is welcome. We cut for all ages and all styles." delay={0.6} isInView={isInView} data-id="element-42" />
              <DifferenceItem icon={<HeartIcon size={22} data-id="element-45" />} title="Community-Rooted" description="Proudly serving Hialeah Gardens — we're not just a barbershop, we're your neighbors." delay={0.7} isInView={isInView} data-id="element-44" />
            </div>
          </div>

          {/* Right: Visual accent */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={isInView ? {
          opacity: 1,
          scale: 1
        } : {}} transition={{
          duration: 0.7,
          delay: 0.2
        }} className="hidden lg:flex items-center justify-center" data-id="element-46">
            <div className="relative" data-id="element-47">
              {/* Decorative rings */}
              <div className="absolute inset-0 rounded-full border-2 border-[#c41e3a]/10 scale-110" aria-hidden="true" data-id="element-48" />
              <div className="absolute inset-0 rounded-full border border-[#c9a84c]/10 scale-125" aria-hidden="true" data-id="element-49" />
              {/* Logo centered */}
              <div className="w-72 h-72 rounded-full bg-black border border-[#1c1c1c] flex items-center justify-center p-8" data-id="element-50">
                <img src="https://cdn.magicpatterns.com/uploads/bJUmpUCmbSxVNo15R16M2H/logo_b.png" alt="Jonathan's Barbershop logo" className="w-full h-full object-contain" data-id="element-51" />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-4 -right-4 bg-[#c41e3a] rounded-lg px-4 py-3 shadow-lg" data-id="element-52">
                <p className="text-white font-bold text-sm" style={{
                fontFamily: 'Oswald, sans-serif',
                letterSpacing: '0.06em'
              }} data-id="element-53">
                  QUALITY
                </p>
                <p className="text-white/80 font-bold text-sm" style={{
                fontFamily: 'Oswald, sans-serif',
                letterSpacing: '0.06em'
              }} data-id="element-54">
                  PRECISION
                </p>
                <p className="text-white/80 font-bold text-sm" style={{
                fontFamily: 'Oswald, sans-serif',
                letterSpacing: '0.06em'
              }} data-id="element-55">
                  COMMUNITY
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}